import * as React from "react";
import TransactionActivityStreamItems from "./TransactionActivityStreamItems";
import ButtonSecondary from "../../../hiberUiKit/src/atoms/Buttons/ButtonSecondary";
import ActivityStreamUtils from "../../../utils/ActivityStreamUtils";
import {TransactionEventType} from "../../../model/transaction/event/TransactionEvent";
import AccountingNote from "../../../model/invoice/note/AccountingNote";
import TransactionEventTypeObject, {TransactionEventCategory} from "../../../model/transaction/event/TransactionEventTypeObject";
import ActivityFilterDropDown from "../activityStream/activityFilterDropDown/ActivityFilterDropDown";
import Transaction from "../../../model/transaction/Transaction";
import StripePayout from "../../../model/transaction/StripePayout";
import deepEqual = require("deep-equal");
import GoCardlessPayout from "../../../model/transaction/GoCardlessPayout";

export type TransactionActivityItemsType = TransactionEventType | AccountingNote;

interface ComponentProps {
	/**
	 * Transaction.
	 */
	transaction: Transaction | StripePayout | GoCardlessPayout;
	/**
	 * Note id to set active class and focus on it.
	 */
	activeNoteId: string;
	/**
	 * Transaction activity data.
	 */
	events: TransactionEventType[];
	/**
	 * Transaction notes.
	 */
	notes: AccountingNote[];
	/**
	 * Edit note click action.
	 * @param note note to edit.
	 */
	onEditNoteClick: (note: AccountingNote) => void;
	/**
	 * Delete note success callback.
	 * @param note deleted note.
	 */
	onDeleteNoteSuccess: (note: AccountingNote) => void;
}

interface State {
	/**
	 * Number of events to be rendered.
	 */
	numberOfEventsToShow: number;
	/**
	 * Filtered (or not) activity data.
	 */
	filteredActivityData: TransactionActivityItemsType[];
}

type Props = ComponentProps;

/**
 * Transaction activity stream component.
 */
export default class TransactionActivityStream extends React.PureComponent<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {
			numberOfEventsToShow: 1,
			filteredActivityData: this.mergeActivityStreamItems(this.props.events, this.props.notes)
		};
	}

	componentWillReceiveProps(newProps: Props) {
		if (!deepEqual(newProps.events, this.props.events)
			|| !deepEqual(newProps.notes, this.props.notes)) {
			this.setState({
				filteredActivityData: this.mergeActivityStreamItems(newProps.events, newProps.notes)
			});
		}
	}

	render() {
		const activityData = this.state.filteredActivityData;
		return (
			<div className="c-common-details_activity-stream-container">
				<ActivityFilterDropDown onSelect={this.filterSingleCategory}
										items={Object.keys(TransactionEventCategory)}/>
				<TransactionActivityStreamItems activityItems={activityData.slice(0, this.state.numberOfEventsToShow)}
												activeNoteId={this.props.activeNoteId}
												onEditNoteClick={this.props.onEditNoteClick}
												onDeleteNoteSuccess={this.props.onDeleteNoteSuccess}
												transaction={this.props.transaction}/>
				{this.state.numberOfEventsToShow < activityData.length &&
				<div className="c-common-details_activity-stream-container_show-more-button">
					<ButtonSecondary label={"Show more"}
									 onClick={() => this.setState({numberOfEventsToShow: this.state.numberOfEventsToShow + 5})}/>
				</div>}
			</div>
		)
	}

	/**
	 * Filter single category.
	 * @param {string} filter key of the category.
	 * @return {TransactionActivityItemsType[]} Filtered data by key.
	 */
	filterSingleCategory = (filter: string) => {
		const activityData = this.mergeActivityStreamItems(this.props.events, this.props.notes);
		const filteredActivityData = activityData.filter(ad =>
			TransactionEventTypeObject.getTypesForCategory(filter)
				.map((ito: TransactionEventTypeObject) => ito.id).indexOf(ad.type) > -1);
		this.setState({filteredActivityData});
	};

	/**
	 * Merge activity stream data and sort it by date - latest first.
	 * @param {TransactionEventType[]} events transaction events.
	 * @param {AccountingNote[]} notes transaction notes.
	 */
	mergeActivityStreamItems(events: Array<TransactionEventType>, notes: AccountingNote[]) {
		return [].concat(events).concat(notes).sort(ActivityStreamUtils.sort);
	}
}
